import { collection, collectionGroup, getDocs } from "firebase/firestore";
import { db } from "../../config/firebase";

const DEFAULT_DATE_FIELDS = [
  "createdAt",
  "createDate",
  "updatedAt",
  "updateDate",
  "startDate",
  "endDate",
  "date",
  "timestamp",
  "created",
  "created_on",
  "insertDate",
];

const TRANSCRIBED_DATE_FIELDS = ["createdAt", "createDate", "updatedAt", "timestamp"];
const ASSEMBLY_DATE_FIELDS = ["timestamp", "createdAt", "createDate", "updatedAt"];

const toMillis = (value) => {
  if (!value) return null;

  if (typeof value === "number") {
    return value < 1e12 ? value * 1000 : value;
  }

  if (typeof value?.toDate === "function") {
    return value.toDate().getTime();
  }

  if (typeof value?.seconds === "number") {
    return value.seconds * 1000;
  }

  if (typeof value === "string") {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) return parsed;
  }

  return null;
};

const readFirstDate = (data, fields = DEFAULT_DATE_FIELDS) => {
  if (!data || typeof data !== "object") return null;
  for (const field of fields) {
    const millis = toMillis(data[field]);
    if (millis) return millis;
  }
  return null;
};

const hasNonEmpty = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === "object") return Object.keys(value).length > 0;
  if (typeof value === "string") return value.trim().length > 0;
  return Boolean(value);
};

const hasAnyField = (data, fields) => {
  if (!data || typeof data !== "object") return false;
  return fields.some((field) => hasNonEmpty(data[field]));
};

const toNumber = (value) => {
  if (typeof value === "number") return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

const asArray = (value) => (Array.isArray(value) ? value : []);

const countCollectionLike = (value) => {
  if (!value) return 0;
  if (Array.isArray(value)) return value.length;
  if (typeof value === "object") return Object.keys(value).length;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return 0;
    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed.length;
      } catch (error) {
        // ignore parse errors and continue
      }
    }
    return trimmed.split(",").map((part) => part.trim()).filter(Boolean).length;
  }
  return 0;
};

const buildServiceError = (error, fallbackMessage) => ({
  message: error?.message || fallbackMessage || "Beklenmeyen bir hata oluştu.",
  code: error?.code || "UNKNOWN_ERROR",
});

const mapTranscriptDoc = (docSnap) => {
  const data = docSnap.data() || {};

  return {
    id: docSnap.id,
    createdAt: readFirstDate(data, TRANSCRIBED_DATE_FIELDS),
    meetingId: data?.meetingid || data?.meetingId || data?.topmeetingid || "",
    userId: data?.userid || data?.userId || data?.uid || "",
    sourceLanguage: data?.sourceLanguage || data?.language || "",
    targetLanguage: data?.targetLanguage || data?.translatedLanguage || "",
    duration: data?.duration ? Number(data.duration) || data.duration : null,
  };
};

const mapAssemblyDoc = (docSnap) => {
  const data = docSnap.data() || {};
  const agenda = Array.isArray(data?.agenda) ? data.agenda : [];
  const decisions = Array.isArray(data?.decisions) ? data.decisions : [];
  const hasSpeakersField = hasAnyField(data, [
    "speakers",
    "speaker",
    "speakerCount",
    "speakersCount",
    "speaker_count",
  ]);
  const hasSpeechAnalysisField = hasAnyField(data, [
    "speech_analysis",
    "speechAnalysis",
    "speechanalysis",
    "speech_analysis_count",
    "speechAnalysisCount",
  ]);

  return {
    id: docSnap.id,
    createdAt: readFirstDate(data, ASSEMBLY_DATE_FIELDS),
    meetingId: data?.meetingid || data?.meetingId || "",
    userId: data?.userid || data?.userId || data?.uid || "",
    agendaCount: agenda.length,
    decisionsCount: decisions.length,
    hasAgenda: agenda.length > 0,
    hasDecisions: decisions.length > 0,
    hasSpeakers: hasSpeakersField,
    hasSpeechAnalysis: hasSpeechAnalysisField,
    completed: String(data?.completed || "").toLowerCase() === "completed",
    agendaMatchSummary: data?.agendaMatchSummary || "",
    hasAgendaMatchSummary: hasAnyField(data, ["agendaMatchSummary"]),
  };
};

const safeGetCollectionDocs = async (collectionName) => {
  try {
    const snap = await getDocs(collection(db, collectionName));
    return snap.docs;
  } catch (error) {
    return [];
  }
};

const safeGetCollectionDocsWithFallback = async (collectionNames) => {
  for (const name of collectionNames) {
    const docs = await safeGetCollectionDocs(name);
    if (docs.length > 0) return docs;
  }
  return [];
};

const safeGetCollectionGroupDocs = async (collectionName) => {
  try {
    const snap = await getDocs(collectionGroup(db, collectionName));
    return snap.docs;
  } catch (error) {
    return [];
  }
};

const safeGetCollectionGroupDocsWithFallback = async (collectionNames) => {
  for (const name of collectionNames) {
    const docs = await safeGetCollectionGroupDocs(name);
    if (docs.length > 0) return docs;
  }
  return [];
};

const buildConnectionsFromUsers = (userDocs) => {
  const uniqueConnections = new Set();
  const uniqueConnectionRequests = new Set();

  for (const userDoc of userDocs) {
    const data = userDoc.data() || {};
    const userId = String(data?.userId || userDoc.id || "");

    if (Array.isArray(data?.connections)) {
      data.connections.forEach((targetId) => {
        if (!targetId) return;
        const key = [userId, String(targetId)].sort().join("::");
        uniqueConnections.add(key);
      });
    }

    if (Array.isArray(data?.connectionsReq)) {
      data.connectionsReq.forEach((targetId) => {
        if (!targetId) return;
        const key = `${userId}->${String(targetId)}`;
        uniqueConnectionRequests.add(key);
      });
    }
  }

  return {
    totalConnections: uniqueConnections.size,
    totalConnectionRequests: uniqueConnectionRequests.size,
  };
};

const mapMeetingDoc = (docSnap) => {
  const data = docSnap.data() || {};
  const blackListRaw = data?.blackList ?? data?.blacklist ?? data?.black_list ?? [];
  const hasChat = hasAnyField(data, [
    "chat",
    "chats",
    "message",
    "messages",
    "meetingChat",
    "meetingChats",
    "conversation",
    "conversations",
    "chatCount",
    "messageCount",
  ]);
  return {
    id: docSnap.id,
    createdAt: readFirstDate(data),
    meetingId: data?.meetingId || docSnap.id || "",
    isPrivate: Boolean(data?.private),
    isPremium: Boolean(data?.premium),
    businessState: Boolean(data?.meetingBusinessState),
    hasChat,
    adminUsersCount: asArray(data?.adminUsers).length,
    moderatorUsersCount: asArray(data?.moderatorUsers).length,
    blackListCount: countCollectionLike(blackListRaw),
    btobMeetingsCount: asArray(data?.btobmeetings).length,
    matchingSurveyUsersCount: asArray(data?.matchingSurveyUsers).length,
    sessionCount: toNumber(data?.sessioncount),
    sessionDeskCount: toNumber(data?.sessiondeskcount),
    sessionBreakDuration: toNumber(data?.sessionbreakduration),
    amount: toNumber(data?.amount),
  };
};

const listMeetingStatisticsData = async () => {
  try {
    const meetingDocs = await safeGetCollectionDocs("meetings");
    const meetings = meetingDocs.map(mapMeetingDoc);
    return {
      meetings,
      fetchedAt: Date.now(),
    };
  } catch (error) {
    throw buildServiceError(error, "Toplantı istatistik verileri yüklenemedi.");
  }
};

const listStatisticsData = async () => {
  try {
    const [meetingDocs, transcriptTopDocs, assemblyTopDocs, userDocs, connectionDocs] = await Promise.all([
      safeGetCollectionDocs("meetings"),
      safeGetCollectionDocsWithFallback([
        "transcribedAudios",
        "transcribedAudio",
        "transcribedaudio",
        "transcribed_audios",
        "transkriptAudios",
        "transkriptAudio",
        "transcriptAudios",
        "transcriptAudio",
        "transcripts",
      ]),
      safeGetCollectionDocs("assemblyAI"),
      safeGetCollectionDocs("users"),
      safeGetCollectionDocsWithFallback([
        "connections",
        "connection",
        "Connections",
        "ConnectionsReq",
      ]),
    ]);

    const [transcriptGroupDocs, assemblyGroupDocs] = await Promise.all([
      transcriptTopDocs.length > 0
        ? Promise.resolve([])
        : safeGetCollectionGroupDocsWithFallback([
            "transcribedAudios",
            "transcribedAudio",
            "transcribedaudio",
            "transcribed_audios",
            "transkriptAudios",
            "transkriptAudio",
            "transcriptAudios",
            "transcriptAudio",
            "transcripts",
          ]),
      assemblyTopDocs.length > 0 ? Promise.resolve([]) : safeGetCollectionGroupDocsWithFallback(["assemblyAI", "assemblyAi", "assemblyai"]),
    ]);

    const transcriptDocs = transcriptTopDocs.length > 0 ? transcriptTopDocs : transcriptGroupDocs;
    const assemblyDocs = assemblyTopDocs.length > 0 ? assemblyTopDocs : assemblyGroupDocs;

    const meetings = meetingDocs.map(mapMeetingDoc);

    const transcripts = transcriptDocs.map(mapTranscriptDoc);
    const assemblyAI = assemblyDocs.map(mapAssemblyDoc);

    const connections = connectionDocs.map((docSnap) => {
      const data = docSnap.data() || {};
      return {
        id: docSnap.id,
        createdAt: readFirstDate(data),
        sourceId: data?.sourceId || data?.fromUserId || data?.userId || "",
        targetId: data?.targetId || data?.toUserId || data?.connectedUserId || "",
      };
    });

    const connectionSummary = buildConnectionsFromUsers(userDocs);

    return {
      meetings,
      transcripts,
      assemblyAI,
      connections,
      connectionSummary,
      fetchedAt: Date.now(),
    };
  } catch (error) {
    throw buildServiceError(error, "İstatistik verileri yüklenemedi.");
  }
};

const statisticsService = {
  listMeetingStatisticsData,
  listStatisticsData,
};

export default statisticsService;
